/**
 * DateTimePickerModal
 *
 * Combined date and time picker in a single modal.
 * Shows calendar carousel on top and scroll time picker on bottom.
 * Maintains local state - only commits on Confirm tap.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Dimensions,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Animated, {
  FadeIn,
  FadeOut,
  ZoomIn,
  ZoomOut,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { BorderRadius, Spacing } from '@/constants/spacing';
import { Colors } from '@/constants/theme';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useKeyboardHeight } from '@/hooks/use-keyboard-height';
import type { DateTimePickerMode, DateTimePickerResult } from '@/types/date-time-picker';

import { CalendarMonthCarousel } from './CalendarMonthCarousel';
import { DateTimePickerFooter } from './DateTimePickerFooter';
import { ScrollTimePicker } from './ScrollTimePicker';

const SCREEN_WIDTH = Dimensions.get('window').width;
const MODAL_HORIZONTAL_PADDING = Spacing.lg * 2;
const MODAL_WIDTH = Math.min(SCREEN_WIDTH - MODAL_HORIZONTAL_PADDING, 360);

type DateTimePickerModalProps = {
  visible: boolean;
  mode: DateTimePickerMode;
  selectedDate: string;
  selectedTime: string | null;
  onConfirm: (result: DateTimePickerResult) => void;
  onClose: () => void;
  minDate?: string;
  accentColor?: string;
  title?: string;
};

/** Parse "HH:MM" into { hour, minute } */
function parseTime(time: string | null): { hour: number; minute: number } {
  if (!time) {
    const now = new Date();
    return { hour: now.getHours(), minute: Math.round(now.getMinutes() / 5) * 5 };
  }
  const [h, m] = time.split(':').map(Number);
  return { hour: h, minute: Math.round(m / 5) * 5 };
}

/** Format hour + minute back to "HH:MM" */
function formatTime(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function DateTimePickerModal({
  visible,
  mode,
  selectedDate,
  selectedTime,
  onConfirm,
  onClose,
  minDate,
  accentColor,
  title,
}: DateTimePickerModalProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const effectiveAccent = accentColor ?? palette.tint;
  const { keyboardHeight, isKeyboardVisible } = useKeyboardHeight();

  // Shift card up when keyboard is visible
  const keyboardOffset = useSharedValue(0);

  useEffect(() => {
    keyboardOffset.value = withTiming(
      isKeyboardVisible ? -(keyboardHeight / 2) : 0,
      { duration: 250 },
    );
  }, [isKeyboardVisible, keyboardHeight, keyboardOffset]);

  const cardKeyboardStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: keyboardOffset.value }],
  }));

  // Local state - buffered until Confirm
  const [localDate, setLocalDate] = useState(selectedDate);
  const [localHour, setLocalHour] = useState(0);
  const [localMinute, setLocalMinute] = useState(0);

  // Reset local state when modal opens
  useEffect(() => {
    if (visible) {
      setLocalDate(selectedDate);
      const parsed = parseTime(selectedTime);
      setLocalHour(parsed.hour);
      setLocalMinute(parsed.minute);
    }
  }, [visible, selectedDate, selectedTime]);

  const handleTimeChange = useCallback((hour: number, minute: number) => {
    setLocalHour(hour);
    setLocalMinute(minute);
  }, []);

  const handleConfirm = useCallback(() => {
    const result: DateTimePickerResult = {};

    if (mode === 'date' || mode === 'datetime') {
      result.date = localDate;
    }
    if (mode === 'time' || mode === 'datetime') {
      result.time = formatTime(localHour, localMinute);
    }

    onConfirm(result);
  }, [mode, localDate, localHour, localMinute, onConfirm]);

  const showCalendar = mode === 'date' || mode === 'datetime';
  const showTime = mode === 'time' || mode === 'datetime';

  return (
    <Modal
      visible={visible}
      animationType="none"
      transparent
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={styles.overlay}>
        {/* Backdrop */}
        <Animated.View
          entering={FadeIn.duration(75)}
          exiting={FadeOut.duration(75)}
          style={styles.backdrop}
        >
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        </Animated.View>

        {/* Picker Card */}
        <Animated.View
          entering={ZoomIn.duration(100)}
          exiting={ZoomOut.duration(75)}
          style={[styles.card, { backgroundColor: palette.cardBackground }, cardKeyboardStyle]}
        >
          {/* Title */}
          {title ? (
            <View style={styles.titleContainer}>
              <Text
                style={[
                  styles.pickerTitle,
                  { color: palette.text, fontFamily: getBodyFont() },
                ]}
              >
                {title}
              </Text>
            </View>
          ) : null}

          {/* Calendar Section */}
          {showCalendar && (
            <CalendarMonthCarousel
              selectedDate={localDate}
              onDateSelect={setLocalDate}
              accentColor={effectiveAccent}
              palette={palette}
              minDate={minDate}
            />
          )}

          {/* Divider between calendar and time */}
          {showCalendar && showTime && (
            <View style={[styles.divider, { backgroundColor: palette.border }]} />
          )}

          {/* Time Section */}
          {showTime && (
            <View style={styles.timeSection}>
              <ScrollTimePicker
                hour={localHour}
                minute={localMinute}
                onTimeChange={handleTimeChange}
                accentColor={effectiveAccent}
                palette={palette}
              />
            </View>
          )}

          {/* Footer */}
          <DateTimePickerFooter
            onConfirm={handleConfirm}
            onCancel={onClose}
            accentColor={effectiveAccent}
            palette={palette}
          />
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: Spacing.lg,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  card: {
    width: MODAL_WIDTH,
    maxWidth: '100%',
    borderRadius: BorderRadius.lg,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  titleContainer: {
    alignItems: 'center',
    paddingTop: Spacing.md,
    paddingBottom: Spacing.xs,
  },
  pickerTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  divider: {
    height: 1,
    marginHorizontal: Spacing.lg,
  },
  timeSection: {
    paddingVertical: Spacing.md,
  },
});
